import { Link } from "@tanstack/react-router";
import type React from "react";
import { LogoMark } from "@/components/brand/LogoMark";
import { env } from "@/env";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import {
	ANALYTICS_CONSENT_EVENT,
	useAnalyticsConsent,
} from "@/lib/analytics-consent";
import {
	CV_PDF_URL,
	GITHUB_URL,
	LINKEDIN_URL,
	SCHEDULE_VISIO_URL,
} from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";

export default function SiteFooter() {
	const { t, localeParam } = useI18n();
	const { capture } = useAnalytics();
	const { state: consent, denyConsent } = useAnalyticsConsent();
	const localeParams: Record<string, string> = localeParam
		? { locale: localeParam }
		: {};

	const track = (cta: string, href: string) => () =>
		capture(ANALYTICS_EVENTS.ctaClick, { cta, placement: "footer", href });

	const linkClass = "text-ink-2 transition-colors hover:text-ink";

	return (
		<footer className="mt-28 overflow-hidden print:hidden border-t border-dashed border-rule-2">
			<div className="mx-auto grid w-full max-w-6xl gap-10 px-5 pt-14 pb-10 md:grid-cols-[1.5fr_1fr_1fr_1fr] md:px-10">
				<div className="space-y-3">
					<Link
						to="/{-$locale}"
						params={localeParams}
						className="flex w-fit items-center gap-3 font-dots text-2xl font-extrabold text-ink"
					>
						<LogoMark className="size-10" />
						{t((t) => t.nav.brand)}
					</Link>
					<p className="max-w-xs text-sm leading-relaxed text-ink-2">
						{t((t) => t.footer.description)}
					</p>
				</div>

				<FooterColumn title={t((t) => t.footer.site)}>
					<Link
						to="/{-$locale}/expo-migration"
						params={localeParams}
						className={linkClass}
					>
						{t((t) => t.nav.expoMigration)}
					</Link>
					<Link
						to="/{-$locale}/questovery"
						params={localeParams}
						className={linkClass}
					>
						{t((t) => t.nav.questovery)}
					</Link>
					<Link
						to="/{-$locale}/about"
						params={localeParams}
						className={linkClass}
					>
						{t((t) => t.nav.about)}
					</Link>
					<Link
						to="/{-$locale}/blog"
						params={localeParams}
						className={linkClass}
						onClick={track("blog", "/blog")}
					>
						{t((t) => t.footer.blog)}
					</Link>
					<Link to="/{-$locale}/cv" params={localeParams} className={linkClass}>
						{t((t) => t.nav.cv)}
					</Link>
				</FooterColumn>

				<FooterColumn title={t((t) => t.footer.contact)}>
					<a
						href={SCHEDULE_VISIO_URL}
						target="_blank"
						rel="noreferrer"
						className={linkClass}
						onClick={track("schedule_call", SCHEDULE_VISIO_URL)}
					>
						{t((t) => t.footer.scheduleCall)}
					</a>
					<a
						href="mailto:simon@lezo.dev"
						className={linkClass}
						onClick={track("email", "mailto:simon@lezo.dev")}
					>
						simon@lezo.dev
					</a>
					<a
						href={LINKEDIN_URL}
						target="_blank"
						rel="noreferrer"
						className={linkClass}
						onClick={track("linkedin", LINKEDIN_URL)}
					>
						{t((t) => t.footer.linkedin)}
					</a>
					<a
						href={GITHUB_URL}
						target="_blank"
						rel="noreferrer"
						className={linkClass}
						onClick={track("github", GITHUB_URL)}
					>
						{t((t) => t.footer.github)}
					</a>
					<a
						href={CV_PDF_URL}
						className={linkClass}
						onClick={track("cv_pdf", CV_PDF_URL)}
					>
						{t((t) => t.footer.cvPdf)}
					</a>
				</FooterColumn>

				<FooterColumn title={t((t) => t.footer.legal)}>
					<Link
						to="/{-$locale}/docs/$slug"
						params={{ ...localeParams, slug: "legal" }}
						className={linkClass}
					>
						{t((t) => t.footer.legalNotice)}
					</Link>
					<Link
						to="/{-$locale}/docs/$slug"
						params={{ ...localeParams, slug: "privacy" }}
						className={linkClass}
					>
						{t((t) => t.footer.privacy)}
					</Link>
					<Link
						to="/{-$locale}/docs"
						params={localeParams}
						className={linkClass}
						onClick={track("docs", "/docs")}
					>
						{t((t) => t.footer.docs)}
					</Link>
					{env.VITE_ANALYTICS_ENABLED && env.VITE_POSTHOG_KEY ? (
						<button
							type="button"
							className={`${linkClass} text-left`}
							onClick={() =>
								window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT))
							}
						>
							{t((t) => t.footer.manageConsent)}
						</button>
					) : null}
					{env.VITE_ANALYTICS_ENABLED &&
					env.VITE_POSTHOG_KEY &&
					consent === "granted" ? (
						<button
							type="button"
							className={`${linkClass} text-left`}
							onClick={denyConsent}
						>
							{t((t) => t.footer.withdrawConsent)}
						</button>
					) : null}
				</FooterColumn>
			</div>

			<p
				className="mx-auto w-full max-w-6xl translate-y-[18%] px-5 font-dots text-[clamp(3.2rem,13vw,10.5rem)] leading-none font-extrabold whitespace-nowrap text-ink/[0.07] select-none md:px-10"
				aria-hidden="true"
			>
				{t((t) => t.nav.brand)}
			</p>
		</footer>
	);
}

function FooterColumn({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<div>
			<p className="mb-3 font-mono text-[0.72rem] font-medium tracking-[0.12em] text-ink uppercase">
				{title}
			</p>
			<div className="flex flex-col gap-2 font-mono text-[0.82rem]">
				{children}
			</div>
		</div>
	);
}
